import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { defineMessages, useIntl } from 'react-intl';
import { Stack } from '@packages/shared';
import { SeoElementLinkList } from '@packages/modules';

const seoElementMessages = defineMessages({
  alternateBrandsHeadline: {
    id: 'seo.alternatebrands.headline',
    defaultMessage: 'Alternative Marken',
    description: 'The headline for the seo alternate links',
  },
  similarLinksHeadline: {
    id: 'seo.similarLinks.headline',
    defaultMessage: 'Empfohlene Kategorien',
    description: 'The headline for the seo similar links',
  },
  relationLinksHeadline: {
    id: 'seo.relationlinks.headline',
    defaultMessage: 'Ähnliche Kategorien',
    description: 'The headline for the seo relation links',
  },
  topLinksHeadline: {
    id: 'seo.topLinks.headline',
    defaultMessage: 'Shopping Tipps',
    description: 'The headline for the seo shopping tip links',
  },
});

const SeoLinksFragment = graphql(/* GraphQL */ `
  fragment SeoLinksFragment on SeoData {
    relationLinks {
      alt
      url
    }
    alternateBrands {
      alt
      url
    }
    topLinks {
      alt
      url
    }
    similarLinks {
      alt
      url
    }
  }
`);

/**
 * SeoElement component renders all existing seo links in an apropriate layout.
 * */
export const SeoElement = ({
  seoLinksData,
}: {
  seoLinksData: FragmentType<typeof SeoLinksFragment>;
}) => {
  const seoLinks = getFragmentData(SeoLinksFragment, seoLinksData);
  const { alternateBrands, similarLinks, relationLinks, topLinks } = { ...seoLinks };
  const { formatMessage } = useIntl();

  if (!(alternateBrands || similarLinks || relationLinks || topLinks)) {
    return null;
  }

  return (
    <Stack rowGap={3}>
      {alternateBrands && (
        <SeoElementLinkList
          linkList={alternateBrands}
          headline={formatMessage(seoElementMessages.alternateBrandsHeadline)}
        />
      )}
      {similarLinks && (
        <SeoElementLinkList
          linkList={similarLinks}
          headline={formatMessage(seoElementMessages.similarLinksHeadline)}
        />
      )}
      {relationLinks && (
        <SeoElementLinkList
          linkList={relationLinks}
          headline={formatMessage(seoElementMessages.relationLinksHeadline)}
        />
      )}
      {topLinks && (
        <SeoElementLinkList
          linkList={topLinks}
          headline={formatMessage(seoElementMessages.topLinksHeadline)}
        />
      )}
    </Stack>
  );
};
